'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { getCookie } from 'cookies-next/client'
import { type AbstractIntlMessages, NextIntlClientProvider } from 'next-intl'

import { setLocaleAction } from './locale.actions'
import { DEFAULT_LOCALE, LOCALES, LOCALE_COOKIE_KEY } from './locale.constants'
import { type Locale } from './locale.types'

type LocaleProviderProps = {
  children: React.ReactNode
  initialLocale: Locale
  /** Messages for `initialLocale`, loaded on the server. Without them, they are imported. */
  initialMessages?: AbstractIntlMessages
}

type LoadedMessages = { locale: Locale; messages: AbstractIntlMessages }

interface LocaleContextType {
  changeLocale: (newLocale: Locale) => void
  locale: Locale
}

const LocaleContext = React.createContext<LocaleContextType>({
  changeLocale: () => {},
  locale: DEFAULT_LOCALE,
})

const LocaleProvider = (props: LocaleProviderProps) => {
  const { children, initialLocale, initialMessages } = props

  const [locale, setLocale] = useState<Locale>(initialLocale)
  const [loaded, setLoaded] = useState<LoadedMessages | null>(
    initialMessages ? { locale: initialLocale, messages: initialMessages } : null,
  )
  const hasMessages = loaded?.locale === locale

  useEffect(() => {
    // Setting a cookie from a server action refreshes the route, and the action waits in
    // the client's action queue. Only write when the cookie is missing or differs (e.g. a
    // `?locale=` override), and never hold the render on it.
    if (getCookie(LOCALE_COOKIE_KEY) !== locale) {
      setLocaleAction(locale).catch((error) => {
        console.error('Failed to save user locale', error)
      })
    }
  }, [locale])

  useEffect(() => {
    if (hasMessages) return

    let isMounted = true

    const initUserLocale = async () => {
      try {
        const translations = await import(`@/translation/${locale}.json`)
        if (isMounted) {
          setLoaded({ locale, messages: translations.default })
        }
      } catch (error) {
        if (isMounted) {
          console.log('Failed to load user locale', error)
        }
      }
    }

    // Errors are handled inside.
    void initUserLocale()

    return () => {
      isMounted = false
    }
  }, [locale, hasMessages])

  const changeLocale = useCallback(async (newLocale: Locale) => {
    if (!LOCALES.includes(newLocale)) {
      throw new Error(`Locale '${newLocale}' is not supported`)
    }

    try {
      const translationPromise = import(`@/translation/${newLocale}.json`)
      const saveLocalePromise = setLocaleAction(newLocale)
      const [importedTranslations] = await Promise.all([
        translationPromise,
        saveLocalePromise,
      ])
      setLoaded({ locale: newLocale, messages: importedTranslations.default })
      setLocale(newLocale)
    } catch (error) {
      console.error(`Failed to change locale to ${newLocale}`, error)
    }
  }, [])

  const value = useMemo(
    () => ({
      changeLocale,
      locale,
    }),
    [changeLocale, locale],
  )

  if (!hasMessages) return null

  return (
    // Explicit time zone: the provider now renders on the server too, and next-intl warns
    // (ENVIRONMENT_FALLBACK) without one. Same value next-intl falls back to.
    <NextIntlClientProvider
      locale={locale}
      messages={loaded.messages}
      timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
    >
      <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
    </NextIntlClientProvider>
  )
}

const useLocale = () => {
  const context = React.useContext(LocaleContext)
  if (context === undefined) {
    throw new Error('useLocale must be used within an LocaleProvider')
  }
  return context
}

export { LocaleProvider, useLocale }
